import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Package,
  CheckCircle,
  AlertTriangle,
} from 'lucide-react';
import { Button } from '@/app/components/ui/button';
import { Badge } from '@/app/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/app/components/ui/card';
import { Skeleton } from '@/app/components/ui/skeleton';
import { Input } from '@/app/components/ui/input';
import { Textarea } from '@/app/components/ui/textarea';
import { Label } from '@/app/components/ui/label';
import { toast } from '@/app/components/ui/simple-toaster';
import { useGearRequest, useReceiveGearRequest } from '@/hooks/use-gear-requests';
import { PageBreadcrumb } from '@/app/components/page-breadcrumb';
import type { GearRequestLine, ReceiveGearRequestLinePayload } from '@/lib/types';

interface LineReceiptState {
  quantity_received: number;
  receipt_notes: string;
  discrepancy_reason: string;
}

export function GearRequestReceiptPage() {
  const { id } = useParams<{ id: string }>();
  const requestId = parseInt(id || '0', 10);
  const navigate = useNavigate();

  const { data: request, isLoading, isError } = useGearRequest(requestId);
  const receiveMutation = useReceiveGearRequest();

  const [overallNotes, setOverallNotes] = useState('');
  const [lineStates, setLineStates] = useState<Record<number, LineReceiptState>>({});

  // Initialize line states when request data loads
  useEffect(() => {
    if (request?.lines) {
      setLineStates((prev) => {
        const next: Record<number, LineReceiptState> = { ...prev };
        for (const line of request.lines) {
          if (!next[line.id]) {
            next[line.id] = {
              quantity_received: line.quantity,
              receipt_notes: '',
              discrepancy_reason: '',
            };
          }
        }
        return next;
      });
    }
  }, [request?.lines]);

  const getLineState = (line: GearRequestLine): LineReceiptState => {
    return lineStates[line.id] || {
      quantity_received: line.quantity,
      receipt_notes: '',
      discrepancy_reason: '',
    };
  };

  const updateLineState = (lineId: number, updates: Partial<LineReceiptState>) => {
    setLineStates((prev) => ({
      ...prev,
      [lineId]: {
        ...prev[lineId],
        ...updates,
      },
    }));
  };

  const handleSubmit = async () => {
    if (!request) return;

    const lines: ReceiveGearRequestLinePayload[] = request.lines.map((line) => {
      const state = getLineState(line);
      // This form owns both fields, so always send them — the backend leaves a
      // value untouched when its key is absent, and an omitted empty string here
      // would make clearing a note impossible.
      return {
        line_id: line.id,
        quantity_received: state.quantity_received,
        receipt_notes: state.receipt_notes,
        discrepancy_reason: state.discrepancy_reason,
      };
    });

    try {
      await receiveMutation.mutateAsync({
        id: requestId,
        data: { lines, notes: overallNotes || undefined },
      });
      toast.success(hasDiscrepancy ? 'Gear Partially Received' : 'Gear Received');
      navigate(`/gear-requests/${requestId}`);
    } catch {
      toast.error('Failed to confirm receipt');
    }
  };

  if (isLoading) {
    return (
      <div className="p-4 sm:p-6 lg:p-8 space-y-6">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (isError || !request) {
    return (
      <div className="p-4 sm:p-6 lg:p-8">
        <div className="text-center py-12">
          <AlertTriangle className="h-12 w-12 mx-auto text-red-500 mb-4" />
          <h2 className="text-xl font-semibold text-gray-900">
            Gear request not found
          </h2>
          <p className="text-gray-600 mt-2">
            The gear request you&apos;re looking for doesn&apos;t exist or you
            don&apos;t have access.
          </p>
          <Button asChild className="mt-4">
            <Link to="/gear-requests">Back to Gear Requests</Link>
          </Button>
        </div>
      </div>
    );
  }

  const canReceive =
    request.status === 'in_transit' || request.status === 'partially_received';

  if (!canReceive) {
    return (
      <div className="p-4 sm:p-6 lg:p-8">
        <div className="text-center py-12">
          <Package className="h-12 w-12 mx-auto text-gray-400 mb-4" />
          <h2 className="text-xl font-semibold text-gray-900">
            Cannot receive this request
          </h2>
          <p className="text-gray-600 mt-2">
            This gear request is currently &ldquo;{request.status}&rdquo; and
            cannot be received. Only in-transit or partially received requests
            can be confirmed.
          </p>
          <Button asChild className="mt-4">
            <Link to={`/gear-requests/${requestId}`}>Back to Details</Link>
          </Button>
        </div>
      </div>
    );
  }

  const hasDiscrepancy = request.lines.some((line) => {
    const state = getLineState(line);
    return state.quantity_received < line.quantity;
  });

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6 max-w-3xl mx-auto">
      <PageBreadcrumb
        items={[
          { label: 'Gear Requests', href: '/gear-requests' },
          { label: `Request #${request.id}`, href: `/gear-requests/${requestId}` },
          { label: 'Confirm Receipt' },
        ]}
      />

      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" asChild>
          <Link to={`/gear-requests/${requestId}`}>
            <ArrowLeft className="h-5 w-5" />
          </Link>
        </Button>
        <div className="flex-1">
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">
            Confirm Receipt
          </h1>
          <p className="text-gray-600">
            Gear Request #{request.id}
          </p>
        </div>
      </div>

      {/* Line items receipt form */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Package className="h-5 w-5" />
            Line Items ({request.lines.length})
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {request.lines.map((line) => {
            const state = getLineState(line);
            const isShort = state.quantity_received < line.quantity;
            return (
              <div
                key={line.id}
                className="border rounded-lg p-4 space-y-3"
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-gray-900 truncate">
                      {line.description}
                    </p>
                    {line.notes && (
                      <p className="text-sm text-gray-500">{line.notes}</p>
                    )}
                  </div>
                  <Badge variant="outline" className="shrink-0">
                    Ordered: {line.quantity}
                  </Badge>
                </div>

                <div className="space-y-2">
                  <Label htmlFor={`qty-${line.id}`}>Quantity Received</Label>
                  <Input
                    id={`qty-${line.id}`}
                    type="number"
                    min={0}
                    max={line.quantity}
                    value={state.quantity_received}
                    onChange={(e) =>
                      updateLineState(line.id, {
                        quantity_received: Math.max(
                          0,
                          Math.min(line.quantity, parseInt(e.target.value) || 0)
                        ),
                      })
                    }
                    className="w-full sm:w-32"
                  />
                </div>

                {isShort && (
                  <div className="space-y-2">
                    <Label
                      htmlFor={`discrepancy-${line.id}`}
                      className="text-orange-700"
                    >
                      Discrepancy Reason
                    </Label>
                    <Textarea
                      id={`discrepancy-${line.id}`}
                      value={state.discrepancy_reason}
                      onChange={(e) =>
                        updateLineState(line.id, {
                          discrepancy_reason: e.target.value,
                        })
                      }
                      placeholder="Why was less received? (damaged, missing, etc.)"
                      rows={2}
                    />
                  </div>
                )}

                <div className="space-y-2">
                  <Label htmlFor={`notes-${line.id}`}>
                    Notes (optional)
                  </Label>
                  <Textarea
                    id={`notes-${line.id}`}
                    value={state.receipt_notes}
                    onChange={(e) =>
                      updateLineState(line.id, {
                        receipt_notes: e.target.value,
                      })
                    }
                    placeholder="Condition notes, storage location, etc."
                    rows={2}
                  />
                </div>
              </div>
            );
          })}
        </CardContent>
      </Card>

      {/* Overall notes */}
      <Card>
        <CardHeader>
          <CardTitle>Overall Receipt Notes</CardTitle>
        </CardHeader>
        <CardContent>
          <Textarea
            value={overallNotes}
            onChange={(e) => setOverallNotes(e.target.value)}
            placeholder="Any general notes about this delivery..."
            rows={3}
          />
        </CardContent>
      </Card>

      {/* Summary & submit */}
      {hasDiscrepancy && (
        <div className="flex items-start gap-3 p-4 bg-orange-50 border border-orange-200 rounded-lg">
          <AlertTriangle className="h-5 w-5 text-orange-600 shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-orange-800">
              Partial receipt detected
            </p>
            <p className="text-sm text-orange-700">
              Some items have quantities below what was ordered. The request
              will be marked as partially received until all items are
              accounted for.
            </p>
          </div>
        </div>
      )}

      <div className="flex flex-col sm:flex-row gap-3 pb-8">
        <Button
          variant="outline"
          asChild
          className="flex-1 sm:flex-none"
        >
          <Link to={`/gear-requests/${requestId}`}>Cancel</Link>
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={receiveMutation.isPending}
          className="flex-1 sm:flex-none"
        >
          <CheckCircle className="h-4 w-4 mr-2" />
          {receiveMutation.isPending
            ? 'Confirming...'
            : hasDiscrepancy
              ? 'Confirm Partial Receipt'
              : 'Confirm Full Receipt'}
        </Button>
      </div>
    </div>
  );
}
